import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SessionExpiryModal } from "@/components/modals/SessionExpiryModal";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Layout components
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

// Pages are loaded on demand so a visitor only downloads the route they asked for.
// `named` unwraps a named export into the default shape React.lazy expects.
const named = <K extends string, T extends Record<K, React.ComponentType>>(
  loader: () => Promise<T>,
  exportName: K,
) => lazy(() => loader().then((module) => ({ default: module[exportName] })));

const HomePage = named(() => import("@/pages/HomePage"), "HomePage");
const AboutPage = named(() => import("@/pages/AboutPage"), "AboutPage");
const ContactPage = named(() => import("@/pages/ContactPage"), "ContactPage");
const BlogPage = named(() => import("@/pages/BlogPage"), "BlogPage");
const SuccessStoriesPage = named(() => import("@/pages/SuccessStoriesPage"), "SuccessStoriesPage");
const BrandingPage = lazy(() => import("@/pages/BrandingPage"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const FAQsPage = named(() => import("@/pages/FAQsPage"), "FAQsPage");
const Jobs = lazy(() => import("@/pages/Jobs"));
const JobDetail = lazy(() => import("@/pages/jobs/JobDetail"));
const SignupRedirectPage = lazy(() => import("@/pages/schools/SignupRedirectPage"));
const SchoolDashboardPage = named(() => import("@/pages/schools/DashboardPage"), "SchoolDashboardPage");

// Teacher pages
const TeacherJobsPage = named(() => import("@/pages/teachers/JobsPage"), "TeacherJobsPage");
const TeacherProfilePage = named(() => import("@/pages/teachers/ProfilePage"), "TeacherProfilePage");
const TeacherPrivacyPage = named(() => import("@/pages/teachers/PrivacyPage"), "TeacherPrivacyPage");
const TeacherDashboard = lazy(() => import("@/pages/teachers/DashboardPage"));
const ResourcesPage = named(() => import("@/pages/teachers/ResourcesPage"), "ResourcesPage");
const ResourcesLinksPage = named(() => import("@/pages/teachers/resources/ResourcesLinksPage"), "ResourcesLinksPage");
const ResourcesGamesPage = named(() => import("@/pages/teachers/resources/ResourcesGamesPage"), "ResourcesGamesPage");
const ResourcesExamPrepPage = named(() => import("@/pages/teachers/resources/ResourcesExamPrepPage"), "ResourcesExamPrepPage");
const ResourcesKidsPhonicsPage = named(() => import("@/pages/teachers/resources/ResourcesKidsPhonicsPage"), "ResourcesKidsPhonicsPage");
const ResourcesAIToolsPage = named(() => import("@/pages/teachers/resources/ResourcesAIToolsPage"), "ResourcesAIToolsPage");
const CareerGuidancePage = named(() => import("@/pages/teachers/CareerGuidancePage"), "CareerGuidancePage");

// School pages
const PostJobPage = named(() => import("@/pages/schools/PostJobPage"), "PostJobPage");
const BrowseTeachersPage = named(() => import("@/pages/schools/BrowseTeachersPage"), "BrowseTeachersPage");
const PremiumListingsPage = named(() => import("@/pages/schools/PremiumListingsPage"), "PremiumListingsPage");
const AiMatchingPage = named(() => import("@/pages/schools/AiMatchingPage"), "AiMatchingPage");
const SchoolProfilePage = named(() => import("@/pages/schools/ProfilePage"), "SchoolProfilePage");
const SubscriptionPage = named(() => import("@/pages/schools/SubscriptionPage"), "SubscriptionPage");

// AI Services
const AiServicesPage = named(() => import("@/pages/AiServicesPage"), "AiServicesPage");

// Legal pages
const PrivacyPolicyPage = named(() => import("@/pages/legal/PrivacyPolicyPage"), "PrivacyPolicyPage");
const TermsOfServicePage = named(() => import("@/pages/legal/TermsOfServicePage"), "TermsOfServicePage");
const CookiePolicyPage = named(() => import("@/pages/legal/CookiePolicyPage"), "CookiePolicyPage");
const SitemapPage = lazy(() => import("@/pages/SitemapPage"));

// Auth pages
const LoginPage = named(() => import("@/pages/auth/LoginPage"), "LoginPage");
const SignUpPage = named(() => import("@/pages/auth/SignUpPage"), "SignUpPage");
const TeacherSignupPage = named(() => import("@/pages/auth/TeacherSignupPage"), "TeacherSignupPage");
const ForgotPasswordPage = named(() => import("@/pages/auth/ForgotPasswordPage"), "ForgotPasswordPage");
const ResetPasswordPage = named(() => import("@/pages/auth/ResetPasswordPage"), "ResetPasswordPage");

// Error pages
const NotFoundPage = named(() => import("@/pages/NotFoundPage"), "NotFoundPage");

// Messaging
const MessagesPage = named(() => import("@/pages/MessagesPage"), "MessagesPage");

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

interface LayoutProps {
  children: React.ReactNode;
}

// Shown while a route's code is downloading. Delayed so a fast chunk does
// not flash a spinner over the page that is about to appear.
const RouteFallback: React.FC = () => {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setShowSpinner(true), 180);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      {showSpinner ? (
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
      ) : null}
    </div>
  );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />

      {/* Session expiry modal */}
      <SessionExpiryModal />
    </div>
  );
};

function AppContent() {
  return (
    <AuthProvider>
      <Layout>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/success-stories" element={<SuccessStoriesPage />} />
          <Route path="/branding" element={<BrandingPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faqs" element={<FAQsPage />} />
          <Route path="/ai-services" element={<AiServicesPage />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/jobs/:id" element={<JobDetail />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/links" element={<ResourcesLinksPage />} />
          <Route path="/resources/games" element={<ResourcesGamesPage />} />
          <Route path="/resources/exam-prep" element={<ResourcesExamPrepPage />} />
          <Route path="/resources/kids-phonics" element={<ResourcesKidsPhonicsPage />} />
          <Route path="/resources/ai-tools" element={<ResourcesAIToolsPage />} />
          <Route
            path="/resources/career-guidance"
            element={<CareerGuidancePage />}
          />

          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/signupteacher" element={<TeacherSignupPage />} />
          <Route path="/schools/signup" element={<SignupRedirectPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sitemap" element={<SitemapPage />} />

          {/* Protected School routes */}
          {/* Legacy path kept so old bookmarks and links still resolve */}
          <Route
            path="/school/dashboard"
            element={<Navigate to="/schools/dashboard" replace />}
          />
          <Route
            path="/schools/dashboard"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <ErrorBoundary>
                  <SchoolDashboardPage />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools/post-job"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <PostJobPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools/browse-teachers"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <BrowseTeachersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools/premium"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <PremiumListingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools/ai-matching"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <AiMatchingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools/profile"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <SchoolProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools/subscription"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <SubscriptionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schools/messages"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL"]}>
                <MessagesPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Teacher routes */}
          <Route
            path="/teacher/dashboard"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ErrorBoundary>
                  <TeacherDashboard />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/jobs"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <TeacherJobsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/profile"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <TeacherProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/privacy"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <TeacherPrivacyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/dashboard"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ErrorBoundary>
                  <TeacherDashboard />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/resources"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ResourcesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/resources/links"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ResourcesLinksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/resources/games"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ResourcesGamesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/resources/exam-prep"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ResourcesExamPrepPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/resources/kids-phonics"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ResourcesKidsPhonicsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/resources/ai-tools"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <ResourcesAIToolsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/career-guidance"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <CareerGuidancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teachers/messages"
            element={
              <ProtectedRoute allowedUserTypes={["TEACHER"]}>
                <MessagesPage />
              </ProtectedRoute>
            }
          />

          {/* Messages route - redirects based on user type */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute allowedUserTypes={["SCHOOL", "TEACHER"]}>
                <MessagesPage />
              </ProtectedRoute>
            }
          />

          {/* Legal routes */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </Layout>
    </AuthProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>

      {/* Global toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#fff",
            color: "#363636",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
            borderRadius: "12px",
            padding: "16px",
          },
          success: {
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
